/**
 * kmlGeometry — reading .kmz / .kml files and measuring what is inside them.
 *
 * The KMZ is now the single source of an asset's geography: a linear asset
 * takes its length from the imported path, and a building takes its footprint
 * from the imported corners. Both the Asset Setup form and the standalone
 * route importer read files through here, so there is one parser and one set
 * of measurements behind both.
 *
 * No React, no Firestore — the parsing and the maths are testable on their own.
 */

// Distance comes from routeGeo so the length an import reports and the length
// the satellite plan draws to are measured by the same function, on the same
// (WGS84) earth radius.
import { haversineMetres, cumulativeDistances } from "./routeGeo";

export { haversineMetres };

const toRad = (d) => (d * Math.PI) / 180;

/**
 * Metres per degree of latitude on the same sphere `haversineMetres` uses.
 * Area and perimeter have to share an earth model — measuring the north side
 * of a footprint one way and its area another put the two roughly 0.7% apart
 * on the same building.
 */
export const M_PER_DEG_LAT = (2 * Math.PI * 6378137) / 360;

/* ------------------------------------------------------------------ */
/* Distance / area                                                     */
/* ------------------------------------------------------------------ */

/** Total length along an ordered list of coordinates, in metres. */
export function pathLengthMetres(coords) {
  const cum = cumulativeDistances(coords || []);
  return cum[cum.length - 1] || 0;
}

const sameCoord = (a, b) =>
  !!a && !!b && Math.abs(a.lat - b.lat) < 1e-9 && Math.abs(a.lng - b.lng) < 1e-9;

/** True when the first and last vertex are the same point. */
export const isClosedRing = (ring) =>
  (ring?.length || 0) > 2 && sameCoord(ring[0], ring[ring.length - 1]);

/** A ring with its closing vertex removed — the distinct corners. */
export function ringCorners(ring) {
  if (!ring?.length) return [];
  return isClosedRing(ring) ? ring.slice(0, -1) : [...ring];
}

/** The number of distinct corners in a ring — 4 for a rectangular building. */
export const cornerCount = (ring) => ringCorners(ring).length;

/** Perimeter of a ring, closing it first if the file left it open. */
export function ringPerimeterMetres(ring) {
  const corners = ringCorners(ring);
  if (corners.length < 3) return pathLengthMetres(ring || []);
  return pathLengthMetres([...corners, corners[0]]);
}

/**
 * Plan area of a ring in m², by shoelace on a local equirectangular
 * projection. At building scale the projection error is negligible, and it
 * avoids pulling in a geodesy library for a footprint.
 */
export function ringAreaM2(ring) {
  const corners = ringCorners(ring);
  if (corners.length < 3) return 0;

  const lat0 = corners.reduce((sum, c) => sum + c.lat, 0) / corners.length;
  const mPerDegLat = M_PER_DEG_LAT;
  const mPerDegLng = M_PER_DEG_LAT * Math.cos(toRad(lat0));

  let twiceArea = 0;
  for (let i = 0; i < corners.length; i += 1) {
    const a = corners[i];
    const b = corners[(i + 1) % corners.length];
    const ax = a.lng * mPerDegLng;
    const ay = a.lat * mPerDegLat;
    const bx = b.lng * mPerDegLng;
    const by = b.lat * mPerDegLat;
    twiceArea += ax * by - bx * ay;
  }
  return Math.abs(twiceArea) / 2;
}

/* ------------------------------------------------------------------ */
/* KML parsing                                                         */
/* ------------------------------------------------------------------ */

/** KML coordinates are whitespace-separated `lng,lat[,alt]` tuples. */
export function parseCoordString(text) {
  return String(text || "")
    .trim()
    .split(/\s+/)
    .map((tuple) => {
      const parts = tuple.split(",").map((n) => Number(n));
      if (parts.length < 2 || !Number.isFinite(parts[0]) || !Number.isFinite(parts[1])) return null;
      return {
        lng: parts[0],
        lat: parts[1],
        alt: Number.isFinite(parts[2]) ? parts[2] : null,
      };
    })
    .filter(Boolean);
}

/**
 * Parse KML text into the three geometries an asset can carry:
 *
 *   coords   — every LineString, concatenated into one route
 *   polygons — every Polygon's outer ring (a building footprint)
 *   markers  — standalone Points (valves, manholes, tie-ins)
 *
 * `totalLength` is the route length; it is left at 0 for a file that holds
 * only a footprint, whose measurement is a perimeter and an area instead.
 */
export function parseKml(kmlText) {
  const xml = new DOMParser().parseFromString(kmlText, "text/xml");
  if (xml.getElementsByTagName("parsererror")[0]) throw new Error("KML is not valid XML.");

  const placemarks = Array.from(xml.getElementsByTagName("Placemark"));
  const coords = [];
  const markers = [];
  const polygons = [];

  placemarks.forEach((pm) => {
    const name = (pm.getElementsByTagName("name")[0]?.textContent || "").trim();

    // Polygons → footprints. The outer boundary is what a building is drawn
    // from; inner boundaries (courtyards) are not measured here.
    Array.from(pm.getElementsByTagName("Polygon")).forEach((poly) => {
      const outer = poly.getElementsByTagName("outerBoundaryIs")[0] || poly;
      const coordEl = outer.getElementsByTagName("coordinates")[0];
      if (!coordEl) return;
      const ring = parseCoordString(coordEl.textContent);
      if (ring.length >= 3) polygons.push({ name, ring });
    });

    Array.from(pm.getElementsByTagName("LineString")).forEach((ls) => {
      const coordEl = ls.getElementsByTagName("coordinates")[0];
      if (!coordEl) return;
      parseCoordString(coordEl.textContent).forEach((c) => coords.push(c));
    });

    Array.from(pm.getElementsByTagName("Point")).forEach((pt) => {
      const coordEl = pt.getElementsByTagName("coordinates")[0];
      if (!coordEl) return;
      parseCoordString(coordEl.textContent).forEach((c) => markers.push({ ...c, name }));
    });
  });

  return {
    coords,
    markers,
    polygons,
    totalLength: pathLengthMetres(coords),
    placemarks: placemarks.map((p, i) => ({
      index: i,
      name: (p.getElementsByTagName("name")[0]?.textContent || `Placemark ${i + 1}`).trim(),
    })),
  };
}

/** Read a .kml or .kmz File and return its KML text. */
export async function readKmlFile(file) {
  const name = (file?.name || "").toLowerCase();
  if (!name.endsWith(".kml") && !name.endsWith(".kmz")) {
    throw new Error("Please choose a .kml or .kmz file.");
  }
  if (name.endsWith(".kml")) return file.text();

  const JSZip = await ensureJSZip();
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const entry =
    zip.file("doc.kml") ||
    Object.values(zip.files).find((f) => f.name.toLowerCase().endsWith(".kml"));
  if (!entry) throw new Error("KMZ archive contains no .kml file.");
  return entry.async("string");
}

/** Parse a .kml / .kmz File straight through to geometry. */
export async function parseKmlFile(file) {
  return parseKml(await readKmlFile(file));
}

/* ------------------------------------------------------------------ */
/* Turning geometry into an asset's measurements                       */
/* ------------------------------------------------------------------ */

/** Asset types whose extent is a chainage range along a path. */
export const LINEAR_MEASUREMENTS = ["chainage"];

/**
 * Work out what an asset of this type should take from a parsed KMZ.
 *
 * A building is a footprint: the file must carry the corners, and what is
 * measured is the perimeter and the plan area. Everything else is a path: the
 * file carries a route, and what is measured is its length — which for a
 * chainage asset then sets the end chainage.
 *
 * A building drawn as a closed path rather than a polygon still works; the
 * closed LineString is read as the ring.
 *
 * Returns { ok, kind, coords, ring, corners, lengthM, perimeterM, areaM2,
 *           markers, error }.
 */
export function deriveAssetGeometry(parsed, assetType, { measurement } = {}) {
  const isBuilding = assetType === "building" || measurement === "floors";
  const polygons = parsed?.polygons || [];
  const coords = parsed?.coords || [];
  const markers = parsed?.markers || [];

  if (isBuilding) {
    // Prefer a real Polygon; fall back to a closed path, then to 3+ points
    // dropped as corners, which is how some field apps export a footprint.
    let ring = polygons[0]?.ring || null;
    if (!ring && coords.length >= 3) ring = coords;
    if (!ring && markers.length >= 3) ring = markers;

    const corners = ringCorners(ring || []);
    if (corners.length < 3) {
      return {
        ok: false,
        kind: "footprint",
        error:
          "A building needs its footprint — draw the four corners of the building as a Polygon in Google Earth and save that as the KMZ.",
      };
    }

    const closed = [...corners, corners[0]];
    return {
      ok: true,
      kind: "footprint",
      coords: closed,
      ring: closed,
      corners,
      lengthM: 0,
      perimeterM: ringPerimeterMetres(closed),
      areaM2: ringAreaM2(closed),
      markers,
      warning:
        corners.length === 4
          ? ""
          : `The footprint has ${corners.length} corners, not 4 — check it is the building outline.`,
    };
  }

  // Linear / area assets take the path. A file holding only a polygon still
  // gives a usable route: its boundary.
  let route = coords;
  if (route.length < 2 && polygons[0]?.ring?.length >= 2) route = polygons[0].ring;

  if (route.length < 2) {
    return {
      ok: false,
      kind: "path",
      error:
        "No route found. Draw the asset as a Path in Google Earth, right-click it → Save Place As → KMZ.",
    };
  }

  return {
    ok: true,
    kind: "path",
    coords: route,
    ring: null,
    corners: [],
    lengthM: pathLengthMetres(route),
    perimeterM: 0,
    areaM2: 0,
    markers,
    warning: "",
  };
}

/**
 * The asset fields a derived geometry writes. Start is whatever the user
 * entered (a chainage, a floor, a volume); the end of a chainage asset is the
 * start plus the measured route length, because the KMZ is now what defines
 * how long the asset is.
 */
export function assetFieldsFromGeometry(geometry, { measurement, startValue, fileName } = {}) {
  if (!geometry?.ok) return null;

  const first = geometry.coords[0] || null;
  const last = geometry.coords[geometry.coords.length - 1] || null;

  const fields = {
    routeCoords: geometry.coords,
    routeMarkers: geometry.markers || [],
    routeLengthMetres: Math.round(geometry.lengthM),
    routeSource: fileName || "",
    // Kept in step with the route so anything reading the simple anchors
    // (the satellite plan's fallback, older records) still resolves.
    latStart: first?.lat ?? null,
    lngStart: first?.lng ?? null,
    latEnd: last?.lat ?? null,
    lngEnd: last?.lng ?? null,
    footprintCoords: geometry.kind === "footprint" ? geometry.ring : null,
    footprintCorners: geometry.kind === "footprint" ? geometry.corners.length : null,
    footprintPerimeterM: geometry.kind === "footprint" ? round2(geometry.perimeterM) : null,
    footprintAreaM2: geometry.kind === "footprint" ? round2(geometry.areaM2) : null,
  };

  if (measurement === "chainage") {
    const start = Number(startValue) || 0;
    const end = start + Math.round(geometry.lengthM);
    fields.startChainage = start;
    fields.endChainage = end;
    fields.totalLength = end - start;
  }

  return fields;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/* ------------------------------------------------------------------ */
/* Lazy-load JSZip from CDN if it is not bundled                       */
/* ------------------------------------------------------------------ */

export async function ensureJSZip() {
  if (typeof window !== "undefined" && window.JSZip) return window.JSZip;
  try {
    const mod = await import("jszip");
    return mod.default || mod;
  } catch {
    await new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
      s.onload = resolve;
      s.onerror = () => reject(new Error("Failed to load JSZip from CDN."));
      document.body.appendChild(s);
    });
    if (!window.JSZip) throw new Error("JSZip failed to initialise.");
    return window.JSZip;
  }
}
